import {z} from 'zod';
import type {MessageStatus} from './request';

/**
 * The envelope an operator is handed when they download events — one message or every
 * message of a request, the same shape either way, so whoever receives the file parses
 * one thing rather than guessing from the extension whether it is an event or an archive.
 *
 * Deliberately not what goes to Callie: `docs/openadr-interface.md` §5.1 says the VTN is
 * POSTed the bare `FLEX` event, and it would refuse anything wrapped. This is the format
 * of the handover only.
 */
export const EXPORT_REQUEST_STATUSES = ['Pending', 'Acknowledged'] as const;

export type ExportRequestStatus = (typeof EXPORT_REQUEST_STATUSES)[number];

export const exportRequestStatusSchema = z.enum(EXPORT_REQUEST_STATUSES);

/**
 * Seven stored statuses down to the two a receiver can act on. Everything that is not an
 * acknowledgement is `Pending`: `PUBLISHING_FAILED` and `NOT_ACKNOWLEDGED` are terminal
 * for us but say nothing to the party holding this file about whether the dispatch stands
 * — only an acknowledgement does, so only that one is named.
 */
export const exportRequestStatusOf = (status: MessageStatus): ExportRequestStatus =>
  status === 'ACKNOWLEDGED' ? 'Acknowledged' : 'Pending';

export const eventExportSchema = z.object({
  /** When the file was asked for, not when anything in it was sent. */
  issuedAt: z.iso.datetime(),
  events: z
    .array(
      z.object({
        /**
         * **Ours, and always here.** One message is one `FLEX` event for one resource
         * group, and this id is fixed from the moment the event is built — it survives
         * every retry and does not depend on the publication having succeeded. It is the
         * key to deduplicate on when a receiver imports the same file twice.
         */
        messageId: z.string(),
        requestStatus: exportRequestStatusSchema,
        /**
         * Passed through as stored; the send path is where a body is validated.
         *
         * **`id` is the VTN's and appears once it has one.** OpenADR assigns an event's id
         * at `POST /events`, which is why the body we send carries none — claiming an id in
         * the VTN's namespace is not ours to do. After the `201` that id is the event's
         * identity at GOPACS, so it is filled in here: a receiver holding this file can
         * name the same event GOPACS does. An event that never reached the VTN has no such
         * id, and the field is absent rather than invented.
         */
        flexEvent: z.looseObject({}),
      }),
    )
    .min(1),
});

export type EventExport = z.infer<typeof eventExportSchema>;
